import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import ProductCard from './Product';
import PopularProducts from './PopularProducts';
import { strings } from 'localisation';
import { routerUrls } from 'constants';
import { GlobalContext } from 'context/GlobalState';

// ── Aurum home page ────────────────────────────────────────────────────────
// Faithful port of the "Aurum" mock (white + antique gold). Rendered inside the
// existing site chrome (Layout header + FooterBazar), so this file owns only the
// page body - hero through newsletter. Per the mock, categories/hero/bundle/
// editorial use decorative SVG motifs (no photography); only the bestsellers
// strip shows real product images.

const TILE = 'linear-gradient(158deg,#F6F6F3 0%,#EEEEEA 100%)';
const WRAP = 1220;

// The eyebrow gold, the frame gold, the readable gold - all live in the theme
// (palette.gold + text.*). We reference tokens, never hard-code, so the page
// tracks the design system.

// ── little SVG line motifs (viewBox 0 0 96 96 unless noted) ─────────────────
const svgBase = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
};

function TrustIcon({ name, size = 22 }) {
  const p = { width: size, height: size, viewBox: '0 0 24 24', strokeWidth: 1.4, ...svgBase };
  switch (name) {
    case 'measure':
      return (
        <svg {...p}>
          <path d="M4 20 20 4M8 8 4 4m0 4 4-4M16 16l4 4m0-4-4 4" />
          <circle cx="12" cy="12" r="2.2" />
        </svg>
      );
    case 'cotton':
      return (
        <svg {...p}>
          <path d="M12 3c4 3 6 6 6 10a6 6 0 0 1-12 0c0-4 2-7 6-10Z" />
        </svg>
      );
    case 'truck':
      return (
        <svg {...p}>
          <path d="M2 7h11v8H2zM13 10h5l3 3v2h-8z" />
          <circle cx="7" cy="17" r="1.4" />
          <circle cx="17" cy="17" r="1.4" />
        </svg>
      );
    case 'returns':
      return (
        <svg {...p}>
          <path d="M9 12l2 2 4-4M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Z" />
        </svg>
      );
    case 'palette':
      return (
        <svg {...p}>
          <path d="M12 3a9 9 0 1 0 0 18c1 0 1.6-.8 1.6-1.7 0-1.3.9-1.8 1.9-1.8H17a4 4 0 0 0 4-4c0-4.3-4-7.5-9-7.5Z" />
          <circle cx="8" cy="11" r="1" />
          <circle cx="12" cy="8.5" r="1" />
          <circle cx="16" cy="11" r="1" />
        </svg>
      );
    case 'made':
      return (
        <svg {...p}>
          <path d="M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6z" />
          <path d="M9 11l2 2 4-4" />
        </svg>
      );
    case 'sizes':
      return (
        <svg {...p}>
          <rect x="3" y="6" width="18" height="12" rx="1.5" />
          <rect x="6" y="9" width="8" height="6" rx="1" />
        </svg>
      );
    default:
      return null;
  }
}

// ── shared bits ─────────────────────────────────────────────────────────────
const Wrap = ({ children, sx }) => (
  <Box sx={{ maxWidth: WRAP, mx: 'auto', px: { xs: 2.5, md: 4 }, ...sx }}>{children}</Box>
);

// The theme's overline is 0.69rem - too fine to read comfortably at arm's length, so the
// eyebrows on this page sit a step up. Everything else here is likewise nudged toward body1.
const Eyebrow = ({ children, sx }) => (
  <Typography
    variant="overline"
    sx={{ display: 'block', fontSize: 15, fontWeight: 700, color: 'gold.text', ...sx }}>
    {children}
  </Typography>
);

const Stars = ({ sx }) => (
  <Box component="span" sx={{ color: 'gold.text', letterSpacing: '2px', fontSize: 13, ...sx }}>
    ★★★★★
  </Box>
);

// Small uppercase "Всички →" style link with a gold underline.
const TextLink = ({ to, href, children }) => (
  <Box
    component={to ? Link : 'a'}
    to={to}
    href={href}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      fontSize: 13,
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'text.primary',
      borderBottom: '1px solid',
      borderColor: 'gold.fill',
      pb: 0.5,
      transition: '0.2s',
      '&:hover': { color: 'gold.text', gap: 1.5 }
    }}>
    {children}
  </Box>
);

function SecHead({ eyebrow, title, linkTo, linkText, center }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: center ? 'center' : 'space-between',
        alignItems: center ? 'center' : 'flex-end',
        flexDirection: center ? 'column' : 'row',
        textAlign: center ? 'center' : 'left',
        gap: center ? 1.5 : 2.5,
        mb: { xs: 4, md: 5.25 }
      }}>
      <Box>
        <Eyebrow sx={{ mb: center ? 0 : 1.75 }}>{eyebrow}</Eyebrow>
        <Typography variant="h2">{title}</Typography>
      </Box>
      {linkTo && <TextLink to={linkTo}>{linkText}</TextLink>}
    </Box>
  );
}

// vertical rhythm - first content block after the trust band gets top padding,
// the rest butt up beneath it (matches the mock's `padding-top:0` sequence).
const blockPY = { pt: 0, pb: { xs: 6, md: 10.5 } };
const blockFirstPY = { pt: { xs: 6, md: 10.5 }, pb: { xs: 6, md: 10.5 } };

// ── data ────────────────────────────────────────────────────────────────────
// Compact reassurance strip that sits directly under the hero.
// `w` is the tile's column weight on md+. The middle two carry the longest labels
// ("Oeko-Tex Standard 100", "Стандартни и нестандартни"), so equal 1fr columns wrapped them.
const TRUST = [
  { icon: 'measure', b: 'По индивидуални размери', s: 'Изработка по поръчка', w: 1.15 },
  { icon: 'cotton', b: '100% натурален памук', s: 'Oeko-Tex Standard 100', w: 1 },
  { icon: 'sizes', b: 'Разнообразие от размери', s: 'Стандартни и нестандартни', w: 1.2 },
  { icon: 'made', b: 'Произведено в България', s: 'Изработено с грижа', w: 1.1 }
];

// e.g. "1fr 1.2fr 1.25fr 1fr"
const TRUST_COLUMNS = TRUST.map((t) => `${t.w ?? 1}fr`).join(' ');

// The richer "why us" feature cards (the old site's square copy), shown as a
// designed 2×2 card grid below the categories. Text kept verbatim from the old
// site - incl. its original typos (РАМЕРИ / Естевствени / Абстрактини).
const FEATURES = [
  {
    icon: 'measure',
    title: 'Разнообразие от РАМЕРИ, ЦВЕТОВЕ и ФОРМИ',
    bullets: [
      'Стандартни и НЕСТАНДАРТНИ',
      'Съвсем МАЛКИ и СУПЕРГОЛЕМИ',
      'Едноцветни и с Принт',
      'За възрастни и за деца'
    ]
  },
  {
    icon: 'cotton',
    title: 'Естевствени Материали',
    bullets: [
      '100% Натурален Памук',
      'Хипоалергенни и безопасни',
      'Сертифицирани по Oeko-Tex Standard 100'
    ]
  },
  {
    icon: 'palette',
    title: 'Богат избор от Цветове и Стилове',
    bullets: ['Флорални', 'Геометрични', 'Абстрактини', 'Двулицеви', 'Елегантни едноцветни']
  },
  {
    icon: 'made',
    title:
      'Спално бельо, прецизно изработено в България за Вас, за да пасва перфектно на Вашите любими:',
    bullets: ['Възглавница', 'Матрак', 'Завивка']
  }
];

const STEPS = [
  {
    n: '01',
    h: 'Изберете продукт',
    p: 'Разгледайте калъфки, пликове, чаршафи, чаршафи с ластик. Изберете материал и цвят.'
  },
  {
    n: '02',
    h: 'Посочете размерите',
    p: 'Всеки елемент се изработва след поръчка по зададените размери за безупречно прилягане.'
  },
  {
    n: '03',
    h: 'Изберете място за доставка',
    p: 'Ушиваме по поръчка и доставяме с ECONT или SPEEDY до всяка точка на България.'
  }
];

const REVIEWS = [
  {
    p: '«Най-после чаршаф, който пасва идеално на нестандартния ни матрак. Качеството е разкошно.»',
    who: 'Мария Д.',
    city: 'София'
  },
  {
    p: '«Поръчах пликове по размер за детското креватче. Изработката е прецизна, а памукът - много мек.»',
    who: 'Ивайло П.',
    city: 'Пловдив'
  },
  {
    p: '«Елегантно, качествено и точно по мярка. Точно каквото търсех за спалнята си.»',
    who: 'Десислава Г.',
    city: 'Варна'
  }
];

const materialName = (id) => (id === 1 ? 'Памучен сатен' : id === 2 ? 'Ранфорс' : undefined);

// ── page ──────────────────────────────────────────────────────────────────
export default function Home() {
  const { lang } = useContext(GlobalContext);
  const [bestsellers, setBestsellers] = useState([]);
  const [subscribed, setSubscribed] = useState(false);
  const categoriesRef = useRef(null);

  // The hero button drops the reader into the categories rather than leaving the
  // page. Honour prefers-reduced-motion: jump instead of animating.
  const scrollToCategories = () => {
    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    categoriesRef.current?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // No category filter → global "position_landing" order = the featured products.
        const { data } = await axios.get('/products', { params: { lang: 'bg', page: 0, size: 8 } });
        const list = (data || []).map((product) => {
          product.images?.sort(
            (a, b) => (a.Product_Image?.position ?? 0) - (b.Product_Image?.position ?? 0)
          );
          return product;
        });
        if (active) setBestsellers(list.slice(0, 4));
      } catch (e) {
        if (active) setBestsellers([]);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>GoToBed - Спално бельо по поръчка</title>
        <meta
          name="description"
          content="Чаршафи, пликове и калъфки от 100% памук - ушити по вашите точни размери, в България. Памучен сатен и ранфорс, доставка с ECONT и SPEEDY."
        />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <Box sx={{ bgcolor: 'background.default', color: 'text.primary' }}>
        {/* ── HERO - identical to the Home page hero (image, copy, button).
            The photo is a ::before rather than an <img> so it can cover the
            viewport without the layout reserving space for it. The one difference
            from Home: the button scrolls down to the categories on this page
            instead of navigating away to /produkti. */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            height: { xs: '40vh', md: '78vh' },
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'url(/background2.jpg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'brightness(0.85)',
              zIndex: 1
            }
          }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              // Full-bleed scrim: spans the whole image so the headline always sits on an even,
              // darkened field rather than on whatever happens to be in the photo behind it.
              width: '100%',
              textAlign: 'center',
              color: 'white',
              px: { xs: 2, md: 4 },
              // The gradient spans this box, so a taller box = a taller scrim. minHeight guarantees
              // the fade real estate even when the headline is short; the extra height is all on
              // top, where the fade needs room to dissolve into the photo.
              minHeight: { xs: '55%', md: '60%' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              pt: { xs: 10, md: 16 },
              pb: { xs: 3, md: 6 },
              background:
                'linear-gradient(to top, rgba(38,37,35,0.84) 0%, rgba(38,37,35,0.74) 30%, rgba(38,37,35,0.52) 55%, rgba(38,37,35,0.24) 78%, rgba(38,37,35,0) 100%)'
            }}>
            {/* Smaller than the theme's h1 (clamp(2.2rem, 4.4vw, 3.4rem)): this headline is two
                full sentences, not a short phrase, so it needs a lighter setting to sit calmly at
                the foot of the photo. */}
            <Typography
              sx={{
                color: 'white',
                mb: 0,
                fontSize: { xs: '1.2rem', sm: '1.5rem', md: 'clamp(2.4rem, 2.6vw, 2.2rem)' },
                lineHeight: 1.2,
                fontWeight: 600,
                // The scrim behind the text carries the contrast now, so this is just a light
                // shadow to keep the glyph edges crisp - not the heavy outline it used to need.
                textShadow: '0 1px 3px rgba(0,0,0,0.45)'
              }}>
              Не намирате СПАЛНО БЕЛЬО{' '}
              <Box component="br" sx={{ display: { xs: 'block', md: 'none' } }} /> в точния размер?
              <br />
            </Typography>
            <Typography
              sx={{
                color: 'white',
                mt: 1,
                mb: 0,
                fontWeight: 600,
                fontSize: { xs: '1.6rem', sm: '2rem', md: 'clamp(3rem, 2.6vw, 3rem)' },
                lineHeight: 1.2,
                // The scrim behind the text carries the contrast now, so this is just a light
                // shadow to keep the glyph edges crisp - not the heavy outline it used to need.
                textShadow: '0 1px 3px rgba(0,0,0,0.45)'
              }}>
              Ние го изработваме{' '}
              <Box component="br" sx={{ display: { xs: 'block', md: 'none' } }} />
              ПО ПОРЪЧКА
            </Typography>
          </Box>
        </Box>

        {/* The CTA sits below the photo rather than on top of it, so it never competes with the
            headline for contrast against the image. */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            // Equal space above and below: the button sits centred between the photo and the
            // trust band rather than hugging either one.
            py: { xs: 1.5, md: 2 }
          }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={scrollToCategories}
            sx={{
              // The theme's contained-primary is dark ink on gold; this one reads white.
              color: '#FFFFFF',
              '&:hover': { color: '#FFFFFF' },
              // Bigger than the theme default (12px/26px padding, 0.78rem type): this is the
              // hero's single call to action, so it earns the extra weight.
              px: { xs: 4, md: 6 },
              py: { xs: 1.75, md: 2.25 },
              fontSize: { xs: '0.9rem', md: '1rem' }
            }}>
            Разгледайте колекцията
          </Button>
        </Box>

        {/* ── TRUST BAND ───────────────────────────────────────────────── */}
        <Box
          sx={{
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}>
          <Wrap
            sx={{
              px: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: TRUST_COLUMNS }
            }}>
            {TRUST.map((t, i) => (
              <Box
                key={t.b}
                sx={{
                  py: 3.75,
                  // Tighter gutters and icon gap on md+: every px here goes to the labels.
                  px: { xs: 3, md: 2 },
                  display: 'flex',
                  gap: { xs: 2, md: 1.5 },
                  alignItems: 'center',
                  justifyContent: { xs: 'flex-start', md: 'center' },
                  // md puts every tile on one row, so only the first has no left rule
                  borderLeft: { md: i === 0 ? 0 : '1px solid' },
                  borderTop: {
                    xs: i === 0 ? 0 : '1px solid',
                    sm: i < 2 ? 0 : '1px solid',
                    md: 0
                  },
                  borderColor: 'divider'
                }}>
                <Box sx={{ color: 'gold.fill', flex: 'none' }}>
                  <TrustIcon name={t.icon} size={28} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{ fontSize: 17, fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                    {t.b}
                  </Box>
                  <Box sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }}>{t.s}</Box>
                </Box>
              </Box>
            ))}
          </Wrap>
        </Box>

        {/* ── CATEGORIES - Aurum section heading + home page image tiles ───
            Scroll target for the hero button. Only a small scrollMarginTop: the site header
            does not stick, so a large offset just parked the scroll short of the tiles. The
            section's own pt (48px / 84px) already supplies the breathing room above the heading. */}
        <Box
          component="section"
          id="categories"
          ref={categoriesRef}
          sx={{ ...blockFirstPY, scrollMarginTop: { xs: '12px', md: '20px' } }}>
          <Wrap>
            <SecHead
              eyebrow="Колекция"
              title="Разгледайте по категория"
              // linkTo={routerUrls.products}
              // linkText="Всички →"
            />
            <PopularProducts showHeader={false} />
          </Wrap>
        </Box>

        {/* ── OFFERINGS - "what we offer" (centred, matches the page) ────── */}
        <Box component="section" sx={blockPY}>
          <Wrap>
            <SecHead center eyebrow="Създадено за Вашия комфорт" title="Какво предлагаме" />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                gap: { xs: 2.5, md: 3 }
              }}>
              {FEATURES.map((f) => (
                <Box
                  key={f.title}
                  sx={{
                    display: 'flex',
                    gap: { xs: 2, md: 2.5 },
                    p: { xs: 3, md: 3.75 },
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    transition: '0.25s ease',
                    '&:hover': {
                      borderColor: 'gold.fill',
                      boxShadow: '0 24px 50px -34px rgba(33,29,23,0.28)'
                    }
                  }}>
                  <Box
                    sx={{
                      flex: 'none',
                      width: 50,
                      height: 50,
                      borderRadius: '14px',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'gold.text',
                      background:
                        'linear-gradient(145deg, rgba(245,120,25,0.18), rgba(245,120,25,0.05))',
                      border: '1px solid',
                      borderColor: 'gold.soft'
                    }}>
                    <TrustIcon name={f.icon} size={25} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: { xs: 15.5, md: 16.5 },
                        fontWeight: 600,
                        color: 'text.primary',
                        lineHeight: 1.35,
                        mb: 1.5
                      }}>
                      {f.title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {f.bullets.map((li, k) => (
                        <Box
                          key={k}
                          component="span"
                          sx={{
                            fontSize: 15.5,
                            color: 'text.secondary',
                            bgcolor: 'background.default',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 999,
                            px: 1.5,
                            py: 0.5,
                            lineHeight: 1.5
                          }}>
                          {li}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          </Wrap>
        </Box>

        {/* ── BESTSELLERS (real products) ──────────────────────────────── */}
        {/* {bestsellers.length > 0 && (
          <Box component="section" sx={blockPY}>
            <Wrap>
              <SecHead
                eyebrow="Най-продавани"
                title="Любими на клиентите"
                linkTo={routerUrls.products}
                linkText="Вижте всички →"
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                  gap: { xs: 2, md: 3 }
                }}>
                {bestsellers.map((p) => (
                  <ProductCard
                    key={p.id}
                    Product={p}
                    materialName={materialName(p.materialAttributeId)}
                  />
                ))}
              </Box>
            </Wrap>
          </Box>
        )} */}

        {/* ── BUNDLE CTA ───────────────────────────────────────────────── */}
        {/* <Box component="section" sx={blockPY}>
          <Wrap>
            <Box
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '4px',
                p: { xs: 4.5, md: 6.5 },
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr auto' },
                gap: { xs: 4, md: 5 },
                alignItems: 'center'
              }}>
              <Box>
                <Eyebrow sx={{ mb: 1.75 }}>Спестете с комплект</Eyebrow>
                <Typography variant="h2" sx={{ fontSize: { xs: '1.7rem', md: '2.35rem' } }}>
                  Съберете пълния комплект
                </Typography>
                <Typography sx={{ color: 'text.secondary', my: 2, maxWidth: '42ch', fontSize: 16 }}>
                  Чаршаф с ластик, плик за завивка и две калъфки - в един тон, по вашите размери.
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: 1,
                    fontFamily: (t) => t.typography.h2.fontFamily,
                    fontSize: 22,
                    fontWeight: 700,
                    color: 'gold.text',
                    mb: 1
                  }}>
                  −15%
                  <Box
                    component="span"
                    sx={{ fontFamily: 'inherit', fontSize: 14, color: 'text.secondary' }}>
                    <Box component="span" sx={{ fontFamily: (t) => t.typography.body1.fontFamily }}>
                      при поръчка на комплект
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Button
                    component={Link}
                    to={routerUrls.products}
                    variant="contained"
                    color="primary">
                    Създайте комплект
                  </Button>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                {['fitted', 'duvet', 'pillow'].map((m, i) => (
                  <Box
                    key={m}
                    sx={{
                      width: 120,
                      aspectRatio: '4 / 5',
                      background: TILE,
                      boxShadow: 'inset 0 0 0 1px #ECEBE7',
                      borderRadius: '3px',
                      display: 'grid',
                      placeItems: 'center',
                      color: 'gold.fill',
                      ml: i === 0 ? 0 : '-24px'
                    }}>
                    <svg width="46" height="46" viewBox="0 0 96 96" strokeWidth="1.6" {...svgBase}>
                      {m === 'fitted' && (
                        <>
                          <path d="M26 42h44v16a8 8 0 0 1-8 8H34a8 8 0 0 1-8-8z" />
                          <path d="M26 42 30 34h36l4 8" />
                        </>
                      )}
                      {m === 'duvet' && (
                        <>
                          <rect x="26" y="26" width="44" height="44" rx="3" />
                          <path d="M40 26v44M56 26v44" opacity=".5" />
                        </>
                      )}
                      {m === 'pillow' && <rect x="22" y="36" width="52" height="26" rx="9" />}
                    </svg>
                  </Box>
                ))}
              </Box>
            </Box>
          </Wrap>
        </Box> */}

        {/* ── HOW IT WORKS ─────────────────────────────────────────────── */}
        <Box component="section" id="kak-rabotim" sx={{ ...blockPY, scrollMarginTop: '90px' }}>
          <Wrap>
            <SecHead center eyebrow="Просто и лесно" title="Как се поръчва по размер" />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: { xs: 3.25, md: 0 }
              }}>
              {STEPS.map((s, i) => (
                <Box
                  key={s.n}
                  sx={{
                    px: { md: 4.25 },
                    pl: { md: i === 0 ? 0 : 4.25 },
                    borderLeft: { md: i === 0 ? 0 : '1px solid' },
                    borderTop: { xs: i === 0 ? 0 : '1px solid', md: 0 },
                    pt: { xs: i === 0 ? 0 : 3, md: 0 },
                    borderColor: 'divider'
                  }}>
                  <Box
                    sx={{
                      fontFamily: (t) => t.typography.h2.fontFamily,
                      fontSize: 42,
                      fontWeight: 700,
                      color: 'gold.text',
                      lineHeight: 1
                    }}>
                    {s.n}
                  </Box>
                  <Typography variant="h3" sx={{ fontSize: 22, mt: 1.75, mb: 1 }}>
                    {s.h}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', fontSize: 18, lineHeight: 1.6 }}>
                    {s.p}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Wrap>
        </Box>

        {/* ── EDITORIAL ────────────────────────────────────────────────── */}
        <Box component="section" sx={blockPY}>
          <Wrap>
            <Box
              sx={{
                position: 'relative',
                borderRadius: '4px',
                overflow: 'hidden',
                background: TILE,
                boxShadow: 'inset 0 0 0 1px #ECEBE7',
                minHeight: 420,
                display: 'flex',
                alignItems: 'center',
                p: { xs: 3.25, md: 7.5 }
              }}>
              <Box
                sx={{
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'gold.fill',
                  p: { xs: 3.75, md: 5.5 },
                  width: '100%',
                  borderRadius: '3px'
                }}>
                <Eyebrow sx={{ mb: 2 }}>Произведено в България</Eyebrow>
                <Typography variant="h2" sx={{ fontSize: { xs: '1.7rem', md: '2.4rem' } }}>
                  Създадено с грижа специално за Вашия дом
                </Typography>
                <Typography sx={{ color: 'text.secondary', mt: 2, fontSize: 20 }}>
                  Всеки елемент се изработва по поръчка от висококачествен 100% памук, за да
                  получите перфектно прилягане, комфорт и качество, които остават с години.
                </Typography>
              </Box>
            </Box>
          </Wrap>
        </Box>

        {/* ── REVIEWS ──────────────────────────────────────────────────── */}
        {/* <Box component="section" sx={blockPY}>
          <Wrap>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                mb: 5,
                flexWrap: 'wrap'
              }}>
              <Box
                sx={{
                  fontFamily: (t) => t.typography.h2.fontFamily,
                  fontSize: 52,
                  fontWeight: 700,
                  lineHeight: 1,
                  color: 'gold.text'
                }}>
                4.9
              </Box>
              <Box>
                <Stars />
                <Box sx={{ fontSize: 13, color: 'text.disabled' }}>от 1 200+ ревюта</Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3
              }}>
              {REVIEWS.map((r) => (
                <Box
                  key={r.who}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '3px',
                    p: 3.75,
                    bgcolor: 'background.paper'
                  }}>
                  <Stars />
                  <Typography
                    sx={{
                      fontFamily: (t) => t.typography.h2.fontFamily,
                      fontSize: 18,
                      lineHeight: 1.55,
                      my: 2,
                      color: 'text.primary'
                    }}>
                    {r.p}
                  </Typography>
                  <Box sx={{ fontSize: 12, color: 'text.disabled' }}>
                    <Box component="b" sx={{ color: 'text.primary' }}>
                      {r.who}
                    </Box>{' '}
                    · {r.city}
                  </Box>
                </Box>
              ))}
            </Box>
          </Wrap>
        </Box> */}

        {/* ── NEWSLETTER ───────────────────────────────────────────────── */}
        {/* <Box component="section" sx={blockPY}>
          <Wrap>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'gold.fill',
                borderRadius: '4px',
                p: { xs: 4.5, md: 7.5 },
                textAlign: 'center',
                background:
                  'radial-gradient(120% 140% at 50% 0%, rgba(245,120,25,.13), transparent 60%), #F8F8F6'
              }}>
              <Eyebrow sx={{ mb: 2 }}>Присъединете се</Eyebrow>
              <Typography variant="h2">
                Вземете{' '}
                <Box component="em" sx={{ fontStyle: 'italic', fontWeight: 700, color: 'gold.text' }}>
                  −10%
                </Box>{' '}
                за първата поръчка
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 2, mb: 3.25, maxWidth: '44ch', mx: 'auto' }}>
                Абонирайте се за нови десени, съвети за грижа и специални оферти - веднъж месечно.
              </Typography>
              {subscribed ? (
                <Typography sx={{ color: 'gold.text', fontWeight: 700 }}>
                  Благодарим! Очаквайте вашия код на посочения имейл.
                </Typography>
              ) : (
                <Box
                  component="form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setSubscribed(true);
                  }}
                  sx={{
                    display: 'flex',
                    gap: 1.25,
                    maxWidth: 480,
                    mx: 'auto',
                    flexDirection: { xs: 'column', sm: 'row' }
                  }}>
                  <Box
                    component="input"
                    type="email"
                    required
                    placeholder="вашият@имейл.bg"
                    aria-label="Имейл"
                    sx={{
                      flex: 1,
                      border: '1px solid',
                      borderColor: 'hairline.strong',
                      bgcolor: 'background.default',
                      borderRadius: '2px',
                      p: '15px 18px',
                      fontFamily: 'inherit',
                      fontSize: 14,
                      color: 'text.primary',
                      '&:focus': { outline: '2px solid #F57819', outlineOffset: '1px' }
                    }}
                  />
                  <Button type="submit" variant="contained" color="primary">
                    Абонирайте ме
                  </Button>
                </Box>
              )}
            </Box>
          </Wrap>
        </Box> */}
      </Box>
    </>
  );
}
